import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, inr, grams } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Barcode,
  MagnifyingGlass,
  Trash,
  Plus,
  Minus,
  Receipt as ReceiptIcon,
  ArrowsLeftRight,
  CreditCard,
  Money,
  Bank,
  DeviceMobile,
  X,
  Printer,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CustomerCombobox from "@/components/CustomerCombobox";
import PrintableInvoice from "@/components/PrintableInvoice";
import { printInvoice, logoAsDataUrl } from "@/lib/printInvoice";

const PAY_MODES = [
  { id: "cash", label: "Cash", icon: Money },
  { id: "upi", label: "UPI", icon: DeviceMobile },
  { id: "card", label: "Card", icon: CreditCard },
  { id: "bank", label: "Bank", icon: Bank },
];

const toNum = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const parsed = Number(v.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function POS() {
  const [products, setProducts] = useState([]);
  const [refs, setRefs] = useState({ metals: [], purities: [] });
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState([]);
  const [oldGold, setOldGold] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [gstPercent, setGstPercent] = useState(3);
  const [roundOff, setRoundOff] = useState(0);
  const [payments, setPayments] = useState([{ mode: "cash", amount: 0 }]);
  const [scanQ, setScanQ] = useState("");
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastBill, setLastBill] = useState(null);
  const [printMode, setPrintMode] = useState("a4");
  const [settings, setSettings] = useState(null);
  const scanRef = useRef(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/metals").then((r) => setRefs((s) => ({ ...s, metals: r.data })));
    api
      .get("/purities")
      .then((r) => setRefs((s) => ({ ...s, purities: r.data })));
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (r.data?.default_gst_percent != null)
        setGstPercent(r.data.default_gst_percent);
      if (r.data?.logo_url)
        logoAsDataUrl(r.data.logo_url).then((d) => (window.__logoDataUrl = d));
    });
    scanRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    if (!scanQ) return products.slice(0, 24);
    const q = scanQ.toLowerCase();
    return products
      .filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.product_code?.toLowerCase().includes(q) ||
          p.barcode?.toLowerCase().includes(q) ||
          p.huid?.toLowerCase().includes(q),
      )
      .slice(0, 24);
  }, [products, scanQ]);

  const addProduct = (p) => {
    const metal = refs.metals.find((m) => m.id === p.metal_id);
    const purity = refs.purities.find((pu) => pu.id === p.purity_id);
    const ratePerGram =
      toNum(metal?.current_rate) * (toNum(purity?.purity_factor) || 1);
    const grossWeight = toNum(p.gross_weight);
    const stoneWeight = toNum(p.stone_weight);
    const netWeight =
      toNum(p.net_weight) || Math.max(grossWeight - stoneWeight, 0);
    const item = {
      product_id: p.id,
      name: p.name,
      purity: purity?.name || "",
      gross_weight: grossWeight,
      stone_weight: stoneWeight,
      net_weight: netWeight,
      rate_per_gram: ratePerGram,
      making_charge: toNum(p.making_charge),
      stone_charge: toNum(p.stone_charge),
      wastage_percent: toNum(p.wastage_percent),
      quantity: 1,
      amount: 0,
    };
    setItems((arr) => [...arr, item]);
    setScanQ("");
    scanRef.current?.focus();
  };

  const updateItem = (idx, patch) => {
    setItems((arr) =>
      arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  };
  const removeItem = (idx) =>
    setItems((arr) => arr.filter((_, i) => i !== idx));

  // Compute line totals locally for live preview
  const totals = useMemo(() => {
    let sub = 0;
    items.forEach((it) => {
      const net =
        toNum(it.net_weight) ||
        Math.max(toNum(it.gross_weight) - toNum(it.stone_weight), 0);
      const metalVal = net * toNum(it.rate_per_gram);
      const wastage = metalVal * (toNum(it.wastage_percent) / 100);
      const line =
        (metalVal +
          wastage +
          toNum(it.making_charge) +
          toNum(it.stone_charge)) *
        Math.max(1, toNum(it.quantity));
      sub += line;
    });
    let oldGoldVal = 0;
    oldGold.forEach((og) => {
      const net =
        toNum(og.gross_weight) *
        (toNum(og.purity_percent) / 100) *
        (1 - toNum(og.deduction_percent) / 100);
      oldGoldVal += net * toNum(og.rate_per_gram);
    });
    const taxable = Math.max(sub - toNum(discount), 0);
    const gstAmt = taxable * (toNum(gstPercent) / 100);
    const grand = taxable + gstAmt - oldGoldVal + toNum(roundOff);
    const paid = payments.reduce((a, p) => a + (Number(p.amount) || 0), 0);
    return {
      sub: +sub.toFixed(2),
      old_gold: +oldGoldVal.toFixed(2),
      gst: +gstAmt.toFixed(2),
      grand: +grand.toFixed(2),
      paid: +paid.toFixed(2),
      balance: +(grand - paid).toFixed(2),
    };
  }, [items, oldGold, discount, gstPercent, roundOff, payments]);

  const addOldGold = () =>
    setOldGold((arr) => [
      ...arr,
      {
        description: "Old gold",
        gross_weight: 0,
        purity_percent: 91.6,
        deduction_percent: 0,
        rate_per_gram:
          refs.metals.find((m) => m.name === "Gold")?.current_rate || 0,
      },
    ]);
  const updateOG = (idx, patch) =>
    setOldGold((arr) =>
      arr.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    );
  const removeOG = (idx) =>
    setOldGold((arr) => arr.filter((_, i) => i !== idx));

  const addPayment = () =>
    setPayments((p) => [...p, { mode: "cash", amount: 0 }]);
  const updatePayment = (i, patch) =>
    setPayments((arr) =>
      arr.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
    );
  const removePayment = (i) =>
    setPayments((arr) => arr.filter((_, idx) => idx !== i));
  const fillPaymentToGrand = () => {
    if (payments.length === 0) {
      setPayments([{ mode: "cash", amount: totals.grand }]);
    } else {
      setPayments((arr) =>
        arr.map((p, i) => (i === 0 ? { ...p, amount: totals.grand } : p)),
      );
    }
  };

  const checkout = async () => {
    if (items.length === 0) {
      toast.error("Add at least one item");
      return;
    }
    try {
      const payload = {
        customer_id: customer?.id || null,
        customer_name: customer?.name || "Walk-in",
        customer_mobile: customer?.mobile || null,
        items: items.map((i) => ({
          ...i,
          gross_weight: Number(i.gross_weight) || 0,
          stone_weight: Number(i.stone_weight) || 0,
          net_weight: Number(i.net_weight) || 0,
          rate_per_gram: Number(i.rate_per_gram) || 0,
          making_charge: Number(i.making_charge) || 0,
          stone_charge: Number(i.stone_charge) || 0,
          wastage_percent: Number(i.wastage_percent) || 0,
          quantity: Number(i.quantity) || 1,
        })),
        old_gold: oldGold.map((o) => ({
          ...o,
          gross_weight: Number(o.gross_weight) || 0,
          purity_percent: Number(o.purity_percent) || 0,
          deduction_percent: Number(o.deduction_percent) || 0,
          rate_per_gram: Number(o.rate_per_gram) || 0,
        })),
        discount: Number(discount) || 0,
        gst_percent: Number(gstPercent) || 0,
        round_off: Number(roundOff) || 0,
        payments: payments
          .filter((p) => Number(p.amount) > 0)
          .map((p) => ({ ...p, amount: Number(p.amount) })),
      };
      const { data } = await api.post("/bills", payload);
      setLastBill(data);
      setShowReceipt(true);
      toast.success(`Bill ${data.invoice_no} saved`);
      // Reset state
      setItems([]);
      setOldGold([]);
      setDiscount(0);
      setRoundOff(0);
      setCustomer(null);
      setPayments([{ mode: "cash", amount: 0 }]);
      api.get("/products").then((r) => setProducts(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save bill");
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-3.5rem)] md:h-screen flex flex-col md:flex-row"
      data-testid="pos-page"
    >
      {/* LEFT: scan + grid + line items */}
      <section className="md:flex-1 flex flex-col border-r border-border min-w-0">
        <div className="p-4 border-b border-border bg-card/40 backdrop-blur-xl">
          <div className="relative">
            <Barcode
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gold"
            />
            <Input
              ref={scanRef}
              data-testid="pos-scan-input"
              autoFocus
              placeholder="Scan barcode / search by code, name or HUID..."
              value={scanQ}
              onChange={(e) => setScanQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered.length > 0) {
                  addProduct(filtered[0]);
                }
              }}
              className="pl-10 h-12 text-base bg-background border-border"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 content-start">
          {filtered.length === 0 ? (
            <div className="col-span-full text-center text-muted-foreground py-12">
              No products match. Add stock via Inventory.
            </div>
          ) : (
            filtered.map((p) => (
              <button
                key={p.id}
                data-testid={`pos-product-${p.id}`}
                onClick={() => addProduct(p)}
                className="text-left p-3 rounded-lg border border-border bg-card hover:border-primary/50 active:scale-[0.98] transition-all"
              >
                <div className="aspect-square rounded-md bg-secondary mb-2 overflow-hidden grid place-items-center">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Plus size={20} className="text-muted-foreground" />
                  )}
                </div>
                <div className="text-sm font-medium truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground font-mono truncate">
                  {p.product_code}
                </div>
                <div className="text-xs text-gold mt-1 tabular-nums">
                  {grams(p.net_weight)} · {inr(p.sale_price)}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Line items table */}
        <div className="border-t border-border bg-card/40 max-h-[40%] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card/95 backdrop-blur-xl">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <th className="px-3 py-2">Item</th>
                <th className="px-2 py-2 text-right">Net Wt</th>
                <th className="px-2 py-2 text-right">Rate/g</th>
                <th className="px-2 py-2 text-right">Making</th>
                <th className="px-2 py-2 text-right">Qty</th>
                <th className="px-2 py-2 text-right">Amount</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-muted-foreground"
                  >
                    No items yet. Scan or click a product above.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => {
                  const net =
                    toNum(it.net_weight) ||
                    Math.max(
                      toNum(it.gross_weight) - toNum(it.stone_weight),
                      0,
                    );
                  const metalVal = net * toNum(it.rate_per_gram);
                  const wastage = metalVal * (toNum(it.wastage_percent) / 100);
                  const line =
                    (metalVal +
                      wastage +
                      toNum(it.making_charge) +
                      toNum(it.stone_charge)) *
                    Math.max(1, toNum(it.quantity));
                  return (
                    <tr
                      key={idx}
                      className="border-t border-border"
                      data-testid={`pos-line-${idx}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{it.name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {it.purity}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          step="0.001"
                          className="h-7 w-20 ml-auto text-right tabular-nums bg-background border-border"
                          value={it.net_weight}
                          onChange={(e) =>
                            updateItem(idx, {
                              net_weight: Number(e.target.value),
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          className="h-7 w-24 ml-auto text-right tabular-nums bg-background border-border"
                          value={it.rate_per_gram}
                          onChange={(e) =>
                            updateItem(idx, {
                              rate_per_gram: Number(e.target.value),
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          step="0.01"
                          className="h-7 w-20 ml-auto text-right tabular-nums bg-background border-border"
                          value={it.making_charge}
                          onChange={(e) =>
                            updateItem(idx, {
                              making_charge: Number(e.target.value),
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() =>
                              updateItem(idx, {
                                quantity: Math.max(1, (it.quantity || 1) - 1),
                              })
                            }
                            className="h-7 w-7 grid place-items-center rounded-md bg-secondary hover:bg-accent"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="w-6 text-center tabular-nums">
                            {it.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateItem(idx, {
                                quantity: (it.quantity || 1) + 1,
                              })
                            }
                            className="h-7 w-7 grid place-items-center rounded-md bg-secondary hover:bg-accent"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {inr(line)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={() => removeItem(idx)}
                          className="text-muted-foreground hover:text-destructive p-1"
                        >
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* RIGHT: customer + totals + payment */}
      <aside className="w-full md:w-[420px] shrink-0 flex flex-col border-t md:border-t-0 md:border-l border-border">
        <div className="p-4 border-b border-border bg-card/40 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Customer
            </Label>
            <div className="mt-1.5">
              <CustomerCombobox value={customer} onChange={setCustomer} />
            </div>
          </div>
        </div>

        {/* Old gold */}
        <div className="p-4 border-b border-border max-h-[200px] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
              <ArrowsLeftRight size={12} /> Old gold exchange
            </div>
            <button
              data-testid="pos-add-old-gold"
              onClick={addOldGold}
              className="text-xs text-gold hover:underline"
            >
              + Add
            </button>
          </div>
          {oldGold.length === 0 && (
            <div className="text-xs text-muted-foreground">
              No old gold added.
            </div>
          )}
          {oldGold.map((og, i) => (
            <div
              key={i}
              className="grid grid-cols-5 gap-1.5 items-center mb-1.5"
            >
              <Input
                className="h-8 text-xs bg-background border-border col-span-1"
                placeholder="Wt"
                type="number"
                value={og.gross_weight}
                onChange={(e) =>
                  updateOG(i, { gross_weight: Number(e.target.value) })
                }
              />
              <Input
                className="h-8 text-xs bg-background border-border col-span-1"
                placeholder="Pur%"
                type="number"
                value={og.purity_percent}
                onChange={(e) =>
                  updateOG(i, { purity_percent: Number(e.target.value) })
                }
              />
              <Input
                className="h-8 text-xs bg-background border-border col-span-1"
                placeholder="Ded%"
                type="number"
                value={og.deduction_percent}
                onChange={(e) =>
                  updateOG(i, { deduction_percent: Number(e.target.value) })
                }
              />
              <Input
                className="h-8 text-xs bg-background border-border col-span-1"
                placeholder="Rate"
                type="number"
                value={og.rate_per_gram}
                onChange={(e) =>
                  updateOG(i, { rate_per_gram: Number(e.target.value) })
                }
              />
              <button
                onClick={() => removeOG(i)}
                className="text-muted-foreground hover:text-destructive justify-self-end"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="p-4 border-b border-border space-y-2.5 text-sm">
          <Row label="Subtotal" value={inr(totals.sub)} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Discount
              </Label>
              <Input
                data-testid="pos-discount"
                type="number"
                className="h-9 bg-background border-border text-right tabular-nums mt-1"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                GST %
              </Label>
              <Input
                data-testid="pos-gst"
                type="number"
                className="h-9 bg-background border-border text-right tabular-nums mt-1"
                value={gstPercent}
                onChange={(e) => setGstPercent(e.target.value)}
              />
            </div>
          </div>
          <Row label={`GST (${gstPercent}%)`} value={inr(totals.gst)} />
          {totals.old_gold > 0 && (
            <Row
              label="Less: Old gold"
              value={`- ${inr(totals.old_gold)}`}
              accent
            />
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Round off
              </Label>
              <Input
                type="number"
                step="0.01"
                className="h-9 bg-background border-border text-right tabular-nums mt-1"
                value={roundOff}
                onChange={(e) => setRoundOff(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Grand Total
            </div>
            <div
              data-testid="pos-grand-total"
              className="font-display text-2xl text-gold tabular-nums"
            >
              {inr(totals.grand)}
            </div>
          </div>
        </div>

        {/* Payments */}
        <div className="p-4 border-b border-border space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Payments
            </div>
            <div className="flex gap-2 text-xs">
              <button
                onClick={fillPaymentToGrand}
                className="text-gold hover:underline"
              >
                Fill total
              </button>
              <button
                onClick={addPayment}
                className="text-gold hover:underline"
              >
                + Split
              </button>
            </div>
          </div>
          {payments.map((p, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center"
            >
              <Select
                value={p.mode}
                onValueChange={(v) => updatePayment(i, { mode: v })}
              >
                <SelectTrigger className="h-9 bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAY_MODES.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                data-testid={`pos-payment-${i}`}
                type="number"
                step="0.01"
                className="h-9 bg-background border-border text-right tabular-nums"
                value={p.amount}
                onChange={(e) => updatePayment(i, { amount: e.target.value })}
              />
              {payments.length > 1 && (
                <button
                  onClick={() => removePayment(i)}
                  className="text-muted-foreground hover:text-destructive p-1"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          <div className="flex justify-between text-xs text-muted-foreground pt-1">
            <span>
              Paid:{" "}
              <span className="tabular-nums text-foreground">
                {inr(totals.paid)}
              </span>
            </span>
            <span>
              Balance:{" "}
              <span
                className={`tabular-nums ${totals.balance > 0 ? "text-destructive" : "text-foreground"}`}
              >
                {inr(totals.balance)}
              </span>
            </span>
          </div>
        </div>

        <div className="p-4 mt-auto">
          <Button
            data-testid="pos-pay-button"
            onClick={checkout}
            className="w-full h-14 text-base bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.99]"
          >
            <ReceiptIcon size={20} weight="duotone" className="mr-2" /> CHARGE{" "}
            {inr(totals.grand)}
          </Button>
        </div>
      </aside>

      {/* Receipt modal */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center justify-between gap-3">
              <span>Invoice {lastBill?.invoice_no}</span>
              <div className="flex gap-1 no-print">
                <Button
                  data-testid="print-a4-btn"
                  size="sm"
                  onClick={() =>
                    printInvoice({
                      bill: lastBill,
                      settings,
                      mode: "a4",
                      logoDataUrl: window.__logoDataUrl,
                    })
                  }
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Printer size={14} className="mr-1" /> A4
                </Button>
                <Button
                  data-testid="print-thermal-btn"
                  size="sm"
                  onClick={() =>
                    printInvoice({
                      bill: lastBill,
                      settings,
                      mode: "thermal",
                      logoDataUrl: window.__logoDataUrl,
                    })
                  }
                  variant="ghost"
                >
                  <Printer size={14} className="mr-1" /> Thermal
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {lastBill && (
            <div>
              <PrintableInvoice
                bill={lastBill}
                settings={settings}
                mode={printMode}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`tabular-nums ${accent ? "text-gold" : ""}`}>{value}</span>
  </div>
);
